import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import brandLogo from "@/assets/regard-de-nature-logo.png";
import { BRAND } from "@/config/brand";
import {
  Phone,
  Check,
  X,
  Lock,
  ArrowLeft,
  ClipboardList,
  Sparkles,
  RefreshCcw,
  Leaf,
  Mail,
  MessageCircle,
  Sprout,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const MonApproche = () => {
  const [contactDialogOpen, setContactDialogOpen] = React.useState(false);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Navigation directe vers la section accompagnements de la page d'accueil
  const accompagnementsHref = "/#accompagnements";

  const etapes = [
    {
      number: "1",
      icon: <ClipboardList className="w-6 h-6" />,
      title: "Le bilan initial",
      intro:
        "Le premier rendez-vous est un temps d'échange approfondi, essentiel pour comprendre votre situation dans sa globalité.",
      listIntro: "Nous abordons notamment :",
      items: [
        "votre mode de vie",
        "votre alimentation",
        "votre sommeil",
        "votre niveau de stress",
        "votre historique de santé",
        "votre quotidien (et celui de votre enfant si vous êtes parent)",
      ],
      outro:
        "Si cela est pertinent, j'utilise également l'iridologie comme outil d'observation complémentaire, afin d'affiner la compréhension de votre terrain.\n\nConcrètement, cela consiste à observer l'iris à l'aide de photographies de vos yeux, réalisées lors du rendez-vous.\n\nL'iridologie ne permet pas d'établir un diagnostic médical, mais vient enrichir l'accompagnement naturopathique.",
      outroItalic: true,
    },
    {
      number: "2",
      icon: <Sparkles className="w-6 h-6" />,
      title: "L'analyse et les recommandations",
      intro:
        "À l'issue du bilan, je vous propose un programme d'hygiène de vie personnalisé, adapté à votre rythme et à vos besoins.",
      listIntro: "Celui-ci peut inclure :",
      items: [
        "des ajustements alimentaires",
        "des conseils en hygiène de vie",
        "des recommandations naturelles",
      ],
      outro: "L'objectif est de vous proposer des actions simples, concrètes et progressives.",
    },
    {
      number: "3",
      icon: <Sprout className="w-6 h-6" />,
      title: "La mise en place",
      intro:
        "Les conseils sont pensés pour s'intégrer dans votre quotidien, sans bouleversement brutal.",
      outro:
        "L'idée n'est pas de tout changer, mais d'avancer étape par étape, de manière réaliste et durable.",
    },
    {
      number: "4",
      icon: <RefreshCcw className="w-6 h-6" />,
      title: "Le suivi",
      intro:
        "Un suivi permet d'ajuster les recommandations en fonction de votre évolution, de vos ressentis et de votre réalité.",
      outro: "L'accompagnement s'adapte dans le temps pour rester cohérent et efficace.",
    },
  ];

  const ceQueJePropose = [
    "Une approche globale et individualisée",
    "Un accompagnement structuré et progressif",
    "Des recommandations personnalisées et évolutives",
    "Un suivi adapté à votre rythme",
  ];

  const ceQueJeNeProposePas = [
    "Aucun diagnostic médical",
    "Aucune promesse de guérison",
    "Aucune solution miracle",
    "Aucun remplacement d'un suivi médical",
  ];

  const pourVousSi = [
    "Vous souhaitez mieux comprendre votre fonctionnement ou celui de votre enfant",
    "Vous recherchez des solutions naturelles adaptées à votre quotidien",
    "Vous êtes prêt(e) à vous impliquer dans votre accompagnement",
    "Vous recherchez des solutions durables plutôt que des résultats rapides",
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border bg-card/95 backdrop-blur-sm">
        <div className="container mx-auto px-3 sm:px-4 py-2 sm:py-3 md:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3">
            <img src={brandLogo} alt={BRAND.name.full} className="w-9 sm:w-10 md:w-12 h-auto object-contain" />
            <div className="text-left">
              <div className="font-cinzel font-bold leading-tight">
                <span className="text-secondary text-sm sm:text-base md:text-lg">{BRAND.name.line1}</span>
                <span className="text-primary text-sm sm:text-base md:text-lg"> {BRAND.name.line2}</span>
              </div>
              <p className="text-[8px] sm:text-[9px] md:text-[10px] font-montserrat uppercase tracking-wide">
                <span className="text-primary">{BRAND.baseline.parts.text1}</span>
                <span className="text-secondary"> {BRAND.baseline.parts.separator} </span>
                <span className="text-primary">{BRAND.baseline.parts.text2}</span>
              </p>
            </div>
          </Link>

          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/">
              <Button variant="ghost" size="sm" className="font-montserrat">
                <ArrowLeft className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Retour</span>
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-muted-foreground hover:text-primary font-montserrat"
              >
                <Lock className="w-3 h-3 mr-1" />
                <span className="hidden sm:inline">Espace Pro</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="py-3 sm:py-4 md:py-6 bg-gradient-to-b from-muted/30 to-background">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <img
              src={brandLogo}
              alt={BRAND.name.full}
              className="w-28 h-28 sm:w-36 sm:h-36 md:w-44 md:h-44 mx-auto object-contain"
            />
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-cinzel font-bold text-primary leading-tight tracking-wide">
              MON APPROCHE
            </h1>
            <div className="w-20 h-px bg-secondary mx-auto" />
            <p className="text-base sm:text-lg md:text-xl text-foreground/80 font-montserrat">
              Voici comment se déroule concrètement un accompagnement.
            </p>
          </div>
        </div>
      </section>

      {/* ÉTAPES */}
      <section className="pt-4 pb-12 sm:pt-6 sm:pb-16 md:pt-8 md:pb-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto space-y-8 sm:space-y-10">
            {etapes.map((etape, index) => (
              <Card
                key={index}
                className="border-primary/20 bg-card hover:shadow-elegant transition-shadow duration-300 overflow-hidden"
              >
                <CardContent className="p-6 sm:p-8 md:p-10">
                  <div className="flex flex-col sm:flex-row gap-6">
                    {/* Numéro et icône */}
                    <div className="flex sm:flex-col items-center sm:items-start gap-4 sm:gap-3 flex-shrink-0">
                      <div className="relative">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-primary flex items-center justify-center text-primary-foreground shadow-md">
                          {etape.icon}
                        </div>
                        <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center text-sm font-bold font-montserrat shadow-sm">
                          {etape.number}
                        </div>
                      </div>
                    </div>

                    {/* Contenu */}
                    <div className="flex-1 space-y-4">
                      <h2 className="text-xl sm:text-2xl md:text-3xl font-cinzel font-bold text-primary uppercase tracking-wide">
                        {etape.title}
                      </h2>
                      <div className="w-12 h-px bg-secondary" />
                      <p className="text-base sm:text-lg text-foreground/80 font-montserrat leading-relaxed">
                        {etape.intro}
                      </p>

                      {etape.items && (
                        <div className="space-y-3 pt-2">
                          {etape.listIntro && (
                            <p className="text-sm sm:text-base text-foreground/70 font-montserrat font-medium">
                              {etape.listIntro}
                            </p>
                          )}
                          <ul className="space-y-2">
                            {etape.items.map((item, i) => (
                              <li key={i} className="flex items-start gap-3 font-montserrat">
                                <Leaf className="w-4 h-4 text-secondary mt-1 flex-shrink-0" />
                                <span className="text-foreground/80">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {etape.outro && (
                        <div className="pt-2 space-y-3">
                          {etape.outro.split("\n\n").map((paragraph, i) => (
                            <p
                              key={i}
                              className={`text-base sm:text-lg text-foreground/80 font-montserrat leading-relaxed ${
                                (etape as any).outroItalic ? "italic" : ""
                              }`}
                            >
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CADRE CLAIR */}
      <section className="py-12 sm:py-16 md:py-20 bg-muted/20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-14">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-cinzel font-bold text-primary uppercase tracking-wide">
              Un cadre clair pour un accompagnement en confiance
            </h2>
            <div className="w-20 h-px bg-secondary mx-auto mt-4" />
          </div>

          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto">
            {/* Ce que je propose */}
            <Card className="border-primary/20 bg-primary-light/30">
              <CardContent className="p-6 sm:p-8">
                <h3 className="text-xl sm:text-2xl font-cinzel font-semibold text-primary mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Check className="w-5 h-5 text-primary" />
                  </div>
                  Ce que je propose
                </h3>
                <ul className="space-y-4 font-montserrat">
                  {ceQueJePropose.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-foreground/80">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Ce que je ne propose pas */}
            <Card className="border-muted-foreground/20 bg-muted/30">
              <CardContent className="p-6 sm:p-8">
                <h3 className="text-xl sm:text-2xl font-cinzel font-semibold text-muted-foreground mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                    <X className="w-5 h-5 text-muted-foreground" />
                  </div>
                  Ce que je ne propose pas
                </h3>
                <ul className="space-y-4 font-montserrat">
                  {ceQueJeNeProposePas.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <X className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* POUR VOUS SI */}
      <section className="py-12 sm:py-16 md:py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto">
            <div className="flex flex-col items-center text-center mb-10">
              <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8 text-secondary" />
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-cinzel font-bold text-primary uppercase tracking-wide">
                Cet accompagnement peut vous convenir si…
              </h2>
              <div className="w-20 h-px bg-secondary mx-auto mt-4" />
            </div>

            <div className="space-y-4">
              {pourVousSi.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-4 sm:p-5 bg-card rounded-lg border border-border shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-primary" />
                  </div>
                  <p className="text-foreground/80 font-montserrat">{item}</p>
                </div>
              ))}
            </div>

            <p className="text-center text-base sm:text-lg text-foreground/70 font-montserrat italic mt-10">
              Cet accompagnement s'adresse aussi bien aux adultes qu'aux familles, avec des adaptations en fonction de
              chaque situation.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mx-auto text-center space-y-8">
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to={accompagnementsHref} className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-gradient-primary text-primary-foreground hover:opacity-90 h-12 sm:h-14 px-6 sm:px-8 font-montserrat"
                >
                  Voir les accompagnements
                </Button>
              </Link>

              <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full sm:w-auto h-12 sm:h-14 px-6 sm:px-8 font-montserrat"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Échanger avec moi
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-cinzel text-primary">Échanger avec moi</DialogTitle>
                    <DialogDescription className="font-montserrat">
                      Choisissez votre mode de contact préféré
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <a
                      href="sms:+33637031866"
                      className="flex items-center gap-4 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                    >
                      <div className="w-12 h-12 bg-primary-light rounded-lg flex items-center justify-center text-primary flex-shrink-0">
                        <MessageCircle className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-semibold text-primary font-montserrat">Par sms</p>
                        <p className="text-sm text-muted-foreground font-montserrat">06 37 03 18 66</p>
                      </div>
                    </a>
                    <a
                      href="mailto:contact@regard-de-nature.fr"
                      className="flex items-center gap-4 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                    >
                      <div className="w-12 h-12 bg-secondary-light rounded-lg flex items-center justify-center text-secondary flex-shrink-0">
                        <Mail className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-semibold text-primary font-montserrat">Par email</p>
                        <p className="text-sm text-muted-foreground font-montserrat">contact@regard-de-nature.fr</p>
                      </div>
                    </a>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 border-t border-border bg-card">
        <div className="container mx-auto px-4 text-center">
          <a
            href="https://www.regard-de-nature.fr"
            className="text-sm text-muted-foreground hover:text-primary font-montserrat"
          >
            ← Retour à l'accueil
          </a>
        </div>
      </footer>
    </div>
  );
};

export default MonApproche;
